import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import SkeletonLoader from 'components/SkeletonLoader';
import { PostText } from 'components/feed/PostFeedItem';
import { format } from 'date-fns';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Label,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import { ReviewsIconDualTone } from 'assets/assetsV1/iconComponents';

import { Avatar } from 'uiToolkitV1/Avatar';
import { Divider } from 'uiToolkitV1/Divider';
import { TagOutlined } from 'uiToolkitV1/Tag/TagOutlined';
import { TextBodyMedium, TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

import { useGetLevelUpDetailsQuery } from '../dataHooks';
import { Activity } from '../types/competencyDetailsAdapter';

const CustomTooltip =
  ({ activities }: { activities: Activity[] }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ({ payload }: TooltipProps<any, string | number>) => {
    const activity = activities?.find((a) => a.createdTS === payload?.[0]?.payload?.createdTS);

    if (!activity) {
      return null;
    }

    return (
      <Flex
        bgColor="#FFFFFF"
        flexDir="column"
        boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.08)"
        borderRadius="8px"
        p={4}
        gap={4}
      >
        <TextLabel bold>Checkin Detail</TextLabel>
        <Flex flexDir="column" gap={1}>
          <TextLabelSmall color="text.g2">Progress as on {format(activity.createdTS, 'MMM dd, yyyy')}</TextLabelSmall>
          {/* @ts-expect-error TagOutlined require other props */}
          <TagOutlined>
            {activity.currentProgress} ({activity.progressPercentage})
          </TagOutlined>
        </Flex>
        <Flex flexDir="column">
          <TextLabelSmall color="text.g2">Source</TextLabelSmall>
          {activity.reviewCycle ? (
            <Flex gap={2} alignItems="center">
              <ReviewsIconDualTone boxSize="28px" color="text.g2" fill="none" />
              <TextBodyMedium bold>{activity.reviewCycle}</TextBodyMedium>
            </Flex>
          ) : (
            activity.employeeProfile && (
              <Flex gap={2} alignItems="center">
                <Avatar
                  size={24}
                  src={activity.employeeProfile.profilePhoto}
                  name={activity.employeeProfile.displayName}
                />

                <TextBodyMedium bold>{activity.employeeProfile.displayName}</TextBodyMedium>
              </Flex>
            )
          )}
        </Flex>
        {activity.comment && (
          <Flex flexDir="column">
            <Divider borderColor="#EBEFF3" mb={4} />
            <TextLabelSmall color="text.g2">Comment</TextLabelSmall>
            {/* @ts-expect-error requires more props */}
            <PostText text={activity.comment.text} macrosRes={activity.comment.macros} />
          </Flex>
        )}
      </Flex>
    );
  };

export const ProgressChart = () => {
  const { data: levelUpCompetencyDetails, isLoading } = useGetLevelUpDetailsQuery();

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!levelUpCompetencyDetails?.activity || levelUpCompetencyDetails.activity.length === 0) {
    return null;
  }

  return (
    <Box mt={4} w="100%" height="200px">
      <ResponsiveContainer maxHeight={200}>
        <AreaChart data={levelUpCompetencyDetails.activity} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis width={2} stroke="lightgrey" dataKey="createdTS" tick={false}>
            <Label value="Time" offset={40} fontSize="10px" />
          </XAxis>
          <YAxis
            dataKey="progressPercentage"
            width={2}
            stroke="lightgrey"
            tick={false}
            style={{
              fontSize: '10px',
            }}
          >
            <Label value="Average Progress" angle={-90} position="left" offset={10} fontSize="10px" />
          </YAxis>
          <Tooltip
            content={CustomTooltip({ activities: levelUpCompetencyDetails.activity })}
            wrapperStyle={{ pointerEvents: 'all', maxWidth: '80%' }}
          />
          <Area
            dot={{ r: 4 }}
            activeDot={{ r: 5 }}
            isAnimationActive={false}
            name="Activity"
            dataKey="progressPercentage"
            stroke="#44812F"
            connectNulls
            fill="#DBF2D3"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};
